/* eslint-disable array-bracket-newline */
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import { utils } from 'ethers';
import BigNumber from 'bignumber.js';
import { Network } from '@kattana-io/common';
import { Web3Provider } from '@ethersproject/providers';
import { LimitOrdersProxy } from '@kattana/limit-orders';
import alert from '../../../assets/images/alert_orange.svg';
import fuel from '../../../assets/images/fuel.svg';
import Text from '../../../atoms/Text';
import Button from '../../../molecules/Button';
import Image from '../../../atoms/Image';
import Loader from '../../common/Loader';
import { numberOnly } from './ParamField';
import {
  formatSettingsValue,
  validateSettingsValue,
} from '../helpers/settingsFormatters';
import { actionAppendTransaction } from '../../../actions/transactions';
import { toIntermediateTransaction } from '../../../helpers/intermediateTransactions';
import { NativeCoin } from '../helpers/nativeCoins';

const MIN_GAS = 0.01;
const MAX_GAS = 10 ** 12;

/**
 * Implements a gas panel with actual balance and ability to refill
 */
interface IGasPanelProps {
  chainId: number | undefined;
  account: string | null | undefined;
  onNotify: (input: any) => void;
}

enum GasPanelState {
  LOADING,
  ERROR,
  OK,
}
const WARNING_LEVEL = 1;

const GasPanel = (props: IGasPanelProps) => {
  const dispatch = useDispatch();

  const [proxyAddress, setProxyAddress] = useState<string>('');
  const [balance, setBalance] = useState<number>(0);
  const [state, setState] = useState<GasPanelState>(GasPanelState.LOADING);
  const [coin, setCoin] = useState<string>('');
  const [nonce, setNonce] = useState(0);
  const [openDeposit, setOpenDeposit] = useState(false);

  /**
   * Find a proxy wallet address for this user in this chain
   * @param account - user wallet
   * @param chainId - user chainId
   */
  const onFetch = async (account: string, chainId: number) => {
    try {
      if (props.account) {
        const lp = new LimitOrdersProxy(chainId);
        const result = await lp.getWalletProxyAddress(props.account);
        if (result) {
          setProxyAddress(result);
          console.log('limit orders: proxy address', result);
        } else {
          setProxyAddress('');
          setState(GasPanelState.ERROR);
        }
      }
    } catch (err) {
      console.error(err);
      setState(GasPanelState.ERROR);
    }
  };

  /**
   * Get balance of native currency (ETH) of proxy wallet
   * @param address - proxy wallet address
   */
  const onFetchBalance = async (address: string) => {
    try {
      const provider = new Web3Provider((window as any).ethereum);
      const balance = await provider.getBalance(address);
      let res = utils.formatEther(balance);
      setBalance(Math.round(Number(res) * 1e4) / 1e4);
      setState(GasPanelState.OK);
    } catch (err) {
      console.warn(err);
      setBalance(0);
      setState(GasPanelState.ERROR);
    }
  };

  /**
   * Create refill transaction
   */
  const onRefill = async (gasAmount: number) => {
    try {
      if (state != GasPanelState.LOADING) {
        setState(GasPanelState.LOADING);
        const payload = gasAmount.toString();
        const provider = new Web3Provider((window as any).ethereum);
        const signer = provider.getSigner();
        const tx = {
          to: proxyAddress,
          value: utils.parseEther(payload),
        };

        const transaction = await signer.sendTransaction(tx);
        dispatch(
          actionAppendTransaction(
            toIntermediateTransaction(transaction.hash, props.chainId)
          )
        );
        console.log(transaction);
        setState(GasPanelState.OK);
        setNonce(nonce + 1);
      }
    } catch (err: any) {
      if (err.message) {
        if (err.message.includes('insufficient funds')) {
          props.onNotify({
            kind: 'error',
            message: `Insufficient funds to deposit 0.1 ${coin}`,
          });
        } else {
          props.onNotify({ kind: 'error', message: err.message });
        }
      }
      console.warn(err);
    } finally {
      setState(GasPanelState.OK);
    }
  };

  useEffect(() => {
    if (props.account) {
      onFetch(props.account, Number(props.chainId));
    }
  }, [props.account, props.chainId, nonce]);

  useEffect(() => {
    if (proxyAddress != '') {
      onFetchBalance(proxyAddress);
    }
  }, [proxyAddress]);

  useEffect(() => {
    const net = Network.fromChainId(props.chainId);
    if (net) {
      const nativeCoin = NativeCoin(props.chainId || 1, new BigNumber(0));
      setCoin(nativeCoin.name);
    }
  }, [props.chainId]);

  /**
   * Fixes attention tooltip
   */
  useEffect(() => {
    ReactTooltip.rebuild();
  }, [state]);

  const isError = state === GasPanelState.ERROR;
  return (
    <div className='gas'>
      {isError ? (
        <div className='row warning__panel'>
          <Image size='s' src={alert} className='sign' />
          <Text size={12} className='text--weight-600 text--orange-400'>
            Could not fetch contract data
          </Text>
        </div>
      ) : (
        <div className='row gas__panel'>
          <div
            className='column__left'
            data-tip='Available gas on your proxy contract'
          >
            <Text size={12}>
              <Image size='s' src={fuel} /> &nbsp;{' '}
              {state === GasPanelState.OK ? balance : ' '} {coin}
            </Text>
          </div>
          <div className='column__right'>
            {state === GasPanelState.LOADING ? (
              <Loader />
            ) : (
              <Button
                size='xs'
                text='deposit gas'
                variant='secondary'
                action={() => setOpenDeposit(true)}
                data-tip='Deposit gas to your proxy contract'
              />
            )}
          </div>
        </div>
      )}
      {state === GasPanelState.OK && balance < WARNING_LEVEL && (
        <>
          <div
            className='row warning__panel'
            data-tip='Action required: deposit gas to proxy contract for order execution'
          >
            <Image size='s' src={alert} className='sign' />
            <Text size={12} className='text--weight-600 text--orange-400'>
              Attention
            </Text>
          </div>
          <div className='row'>
            <Text size={12} className='text--orange-200'>
              Inefficient proxy contract balance. Please top up to proceed with
              limit orders.{' '}
            </Text>
          </div>
        </>
      )}
      {openDeposit && (
        <DepositWindow
          onClose={() => setOpenDeposit(false)}
          ntCurrency={coin}
          onDeposit={onRefill}
        />
      )}
    </div>
  );
};

interface IDepositWindowProps {
  onClose(): void;
  ntCurrency: string;
  onDeposit(input: number): void;
}

const DepositWindow = ({
  onClose,
  ntCurrency,
  onDeposit,
}: IDepositWindowProps) => {
  const depositRef = useRef<HTMLSpanElement>(null);
  const [gasAmount, setGasAmount] = useState(0.1);

  const handleKeyDown = (e: any) => numberOnly(e.nativeEvent);

  const onChange = (value: string | null) => {
    const formattedValue = formatSettingsValue(value || '');
    setGasAmount(formattedValue);
  };

  const handleSave = (value: string) => {
    const formattedValue = formatSettingsValue(value).toFixed(2);
    if (depositRef.current) {
      const isValid = validateSettingsValue(formattedValue, MIN_GAS, MAX_GAS);
      if (isValid) {
        setGasAmount(Number(formattedValue));
        depositRef.current.innerText = formattedValue;
      } else {
        setGasAmount(MIN_GAS);
        depositRef.current.innerText = MIN_GAS.toString();
        depositRef.current.focus();
      }
    }
  };

  return (
    <div className='gas__deposit_window'>
      <div
        className='price_alert__backdrop'
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      />
      <div className='price_alert__create' onClick={(e) => e.stopPropagation()}>
        {/* to prevent from chart reacting to clicks */}
        <div className='row'>
          <Text size={14} className='text--grey-200 text--weight-600'>
            Deposit gas
          </Text>
          <div
            className='params_panel__param__property__icon icon-close'
            onClick={() => onClose()}
          />
        </div>
        <div className='price_alert__create__new'>
          <div className='gas row priceSelector'>
            <div className='gas__deposit'>
              <div
                className={`gas__deposit__item ${
                  gasAmount === 0.1
                    ? 'gas__deposit__item--selected'
                    : 'gas__deposit__item--normal'
                }`}
                onClick={() => handleSave('0.1')}
              >
                <Text size={12}>0.1 {ntCurrency}</Text>
              </div>
              <div
                className={`gas__deposit__item ${
                  gasAmount === 0.5
                    ? 'gas__deposit__item--selected'
                    : 'gas__deposit__item--normal'
                }`}
                onClick={() => handleSave('0.5')}
              >
                <Text size={12}>0.5 {ntCurrency}</Text>
              </div>
              <div
                className={`gas__deposit__item ${
                  gasAmount === 1
                    ? 'gas__deposit__item--selected'
                    : 'gas__deposit__item--normal'
                }`}
                onClick={() => handleSave('1')}
              >
                <Text size={12}>1 {ntCurrency}</Text>
              </div>
            </div>
            <div className='gas params_panel__param input_wrapper'>
              <span className='input_wrapper__editable'>
                <span
                  suppressContentEditableWarning={true}
                  contentEditable={true}
                  onKeyDown={(e) => handleKeyDown(e)}
                  onBlur={(e) => handleSave(e.target.innerText)}
                  onInput={(e) => onChange(e.currentTarget.textContent)}
                  ref={depositRef}
                  className='text--14 text--weight-500 text--grey-200 '
                >
                  0.10
                </span>
              </span>
            </div>
          </div>

          <div className='row'>
            <div className='btnWrapper'>
              <Button
                text='Cancel'
                action={onClose}
                size='s'
                variant='secondary'
              />
            </div>
            <div className='btnWrapper'>
              <Button
                text='Deposit'
                disabled={!validateSettingsValue(gasAmount, MIN_GAS, MAX_GAS)}
                action={() => onDeposit(gasAmount)}
                size='s'
                variant='primary'
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GasPanel;
